import { useLocation } from 'react-router-dom';
import {
  paymentTypeTipList,
  SELECTED_ENTITY_IDS_PARAM,
  FILTER_PARAM_KEYS,
} from 'utils/constants';
import useQuery from 'utils/hooks/useQuery';
import useStore from 'state/store/hooks/useStore';
import { useOrgLevel } from 'utils/hooks/useOrgLevel';
import { EFilterConfigType, IFilterConfig } from 'components/filter/types';

interface UseTipsFiltersArgs {
  dateFromISO: string;
  dateToISO: string;
}

interface UseTipsFiltersReturn {
  filters: IFilterConfig[];
  exportFilters: IFilterConfig[];
  paymentTypeArray: string[];
  storeIdArray: string[];
  amountOption: string;
  firstAmount: string | undefined;
  secondAmount: string | undefined;
  searchString: string | undefined;
}

const useTipsFilters = ({
  dateFromISO,
  dateToISO,
}: UseTipsFiltersArgs): UseTipsFiltersReturn => {
  const location = useLocation();
  const query = useQuery(location?.search);
  const { store } = useStore();
  const isOrgLevel = useOrgLevel();

  const paymentTypeArray =
    query.get(FILTER_PARAM_KEYS.PAYMENT_TYPE)?.split(',') || [];
  const storeIdArray =
    query.get(SELECTED_ENTITY_IDS_PARAM.KEY)?.split(',') || [];
  const amountOption = query.get(FILTER_PARAM_KEYS.CONDITION) || 'equal';
  const firstAmount = query.get(FILTER_PARAM_KEYS.AMOUNT_1) || undefined;
  const secondAmount = query.get(FILTER_PARAM_KEYS.AMOUNT_2) || undefined;
  const searchString = query.get(FILTER_PARAM_KEYS.SEARCH) || undefined;

  const amountValues = [amountOption];
  if (firstAmount) amountValues[1] = firstAmount;
  if (secondAmount) amountValues[2] = secondAmount;

  const storeOptions = store.storesList
    ? store.storesList
        .filter((s) => s.has_access)
        .map((s) => ({ name: s.dba_name, value: s.entity_id }))
    : [];

  const filters: IFilterConfig[] = [
    {
      key: FILTER_PARAM_KEYS.AMOUNT,
      label: 'Amount',
      type: EFilterConfigType.number,
      data: {
        keys: ['condition'],
        values: amountValues,
      },
      selected: Boolean(firstAmount || secondAmount),
    },
    {
      key: FILTER_PARAM_KEYS.PAYMENT_TYPE,
      label: 'Payment Type',
      type: EFilterConfigType.list,
      data: {
        keys: FILTER_PARAM_KEYS.PAYMENT_TYPE,
        options: paymentTypeTipList,
        values: paymentTypeArray,
      },
      selected: paymentTypeArray?.length > 0,
    },
  ];

  if (isOrgLevel && storeOptions.length > 0) {
    filters.push({
      key: SELECTED_ENTITY_IDS_PARAM.KEY,
      label: 'Store',
      type: EFilterConfigType.list,
      data: {
        keys: SELECTED_ENTITY_IDS_PARAM.KEY,
        options: storeOptions,
        values: storeIdArray,
      },
      selected: storeIdArray?.length > 0,
    });
  }

  const dateFilterConfig: IFilterConfig = {
    key: FILTER_PARAM_KEYS.DATE,
    label: 'Create Date',
    type: EFilterConfigType.date,
    data: { values: [dateFromISO, dateToISO] },
    selected: false,
  };

  const exportFilters = [dateFilterConfig].concat(filters);

  return {
    filters,
    exportFilters,
    paymentTypeArray,
    storeIdArray,
    amountOption,
    firstAmount,
    secondAmount,
    searchString,
  };
};

export default useTipsFilters;
